import {
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
  OnDestroy, untracked,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { dashboardRouteDataKey } from 'src/app/shared/services/dashboard.service'
import { ToastService } from '../toasts/toasts.service'
import { Location } from '@angular/common'
import { DashboardConfig } from 'src/app/shared/models/dashboardConfig'
import { FilterGroupState, FilterService } from 'src/app/shared/services/filter/filter.service'
import { DashboardHeaderComponent } from './dashboard-header.component'

const defaultDashboardBaseUrl = 'https://aap-ess-nonprd.cloud.databricks.com'

const dashboardBaseUrlsByHostname: Readonly<Record<string, string>> = {
  'settlement-uat.np.ce.ameren.com': 'https://aap-ess-uat.cloud.databricks.com',
  'settlement.ce.ameren.com': 'https://aap-ess-prd.cloud.databricks.com',
}

const getBaseUrl = () =>
  dashboardBaseUrlsByHostname[document.location.hostname] ?? defaultDashboardBaseUrl

export type DashboardData = Readonly<{
  config: DashboardConfig
  requirementsMet: boolean
  filterModified: boolean
  url: string
}>

@Component({
  selector: 'app-dashboard',
  imports: [
    DashboardHeaderComponent,
  ],
  styles: `
    :host {
      display: flex;
      width: 100%;
      height: calc(100vh - 80px);
      flex-direction: column;

      .dashboard-content {
        display: block;
        flex-basis: 0;
        flex-grow: 1;
      }
    }
  `,
  template: `
    @if (dashboardData()) {
      <app-dashboard-header [data]="dashboardData()!" (onReload)="onReload()" />

      <div class='dashboard-content'>
        <ng-content />
      </div>
    }
  `,
})
export class DashboardComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly location = inject(Location)
  private readonly toast = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly subscription = new Subscription()

  readonly onData = output<DashboardData>()

  protected readonly dashboard = signal<DashboardConfig | undefined>(undefined)

  protected readonly filterGroupState = signal<FilterGroupState>({})
  protected readonly dashboardUrl = signal('')
  protected init = signal(false)

  protected readonly nextDashboardUrl = computed((): string => {
    const dashboard = this.dashboard()
    if (dashboard) {
      const baseUrl = `${getBaseUrl()}/embed/dashboardsv3/${dashboard.dashboard_id}?o=latest`
      const queryString = Object.values(this.filterGroupState())
        .map((f) => f.asDashboardQueryParams())
        .flat()
        .map((qp) => `${qp.name}=${encodeURIComponent(qp.value)}`)
        .join('&')

      return queryString ? `${baseUrl}&${queryString}` : baseUrl
    } else {
      return ''
    }
  })

  protected readonly requirementsMet = computed((): boolean => {
    const dashboard = this.dashboard()
    const params = dashboard?.requirements.params ?? []
    const filterState = this.filterGroupState()
    return params.length === 0 ||
      params.some(fields => fields.every(field => filterState[field]?.asCount() > 0))
  })

  protected onReload = () => {
    this.dashboardUrl.set(this.nextDashboardUrl())
  }

  protected readonly dashboardData = computed((): DashboardData | undefined => {
    const config = this.dashboard()
    if (config) {
      return {
        config,
        filterModified: this.dashboardUrl() !== this.nextDashboardUrl(),
        requirementsMet: this.requirementsMet(),
        url: this.dashboardUrl(),
      }
    } else {
      return undefined
    }
  })

  constructor() {
    this.route.data.subscribe(data => {
      const dashboardConfig = data[dashboardRouteDataKey] as DashboardConfig | undefined
      if (dashboardConfig) {
        this.dashboard.set(dashboardConfig)
      } else {
        this.toast.show({ type: 'error', message: `Could not find dashboard for route: ${this.route}` })
        this.location.back()
      }
    })

    effect(() => {
      const dashboard = this.dashboard()
      if (dashboard) {
        untracked(() => {
          void this.importDashboard(dashboard)
        })
      }
    })

    effect(() => {
      const data = this.dashboardData()
      if (data) {
        untracked(() => {
          this.onData.emit(data)
        })
      }
    })
  }

  private async importDashboard(dashboard: DashboardConfig) {
    this.filterService.importDashboardParams(dashboard)
    const observable = await this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      dashboard.name,
    )
    this.subscription.add(observable.subscribe())
    this.subscription.add(
      this.filterService.groupState(dashboard.name).subscribe(state => {
        this.filterGroupState.set(state)
        if (!this.init()) {
          this.init.set(true)
          // any filter params imported from URL are applied immediately if requirements are met
          // otherwise they aren't applied until the view is reloaded
          if (this.requirementsMet()) {
            this.onReload()
          }
        }
      }),
    )
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }
}
